// src/pages/builder/IntakeFormDynamic.jsx
import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  Box, Container, Typography, Grid, TextField, MenuItem, Button, Card, CardContent,
  Stack, Snackbar, Alert, InputAdornment, FormControl, FormControlLabel, FormHelperText, Checkbox,
} from '@mui/material';

import LandingPageNav from '../../components/landingpage/LandingPageNav';
import Footer from '../../components/Footer/footer';
import { apipostPublic } from '../../service/api';
import ConfettiModal from '../../components/confetti/confettimodal';
import {
  maskPhone,
  isValidPhone,
  maskZip,
  isValidZip,
  isZipAllZeros,
  maskMoney,
  stripMoney,
  maskPercent,
  isValidGenericEmail,
  isValidBuilderEmail,
  builderEmailDomainsLabel,
} from '../../utils/intakeValidation';

const DEFAULT_CFG = {
  slug: '',
  title: 'Lead Intake',
  subtitle: '',
  intro: '',
  brandColor: '#3e4b59',
  logoSrc: '',
  endpoint: '/lead/webhook',
  loanOfficers: [],
  agentNames: [],
  communities: [],
  forwardCommitments: [],
  urgency: [],
  dateFields: [],
  fieldLabelOverrides: {},
  smsOptIn: { enabled: false, consentText: '' },
  customFields: [],
  hiddenFields: [],
};

// Built-in OPTIONAL fields a division config may hide via cfg.hiddenFields.
// Required built-ins are deliberately not hideable.
const HIDEABLE_FIELDS = [
  'taxRate', 'lot', 'price', 'incentiveMax', 'solar', 'hoa',
  'realtorName', 'realtorPhone', 'realtorEmail', 'urgency',
];

export default function IntakeFormDynamic({ config, slug }) {
  const [showConfetti, setShowConfetti] = useState(false);

  // 🔹 Merge backend config with defaults
  const cfg = useMemo(
    () => ({ ...DEFAULT_CFG, ...(config || {}) }),
    [config]
  );

  useEffect(() => {
    document.title = `${cfg.title} | Jet HomeLoans`;
  }, [cfg.title]);

  const [form, setForm] = useState({
    loanOfficer: '',
    agentName: '',
    agentPhone: '',
    agentEmail: '',
    buyerFirst: '',
    buyerLast: '',
    buyerEmail: '',
    buyerPhone: '',
    community: '',
    zip: '',
    lot: '',
    price: '',
    fwdCommit: '',
    estClose: '',
    incentiveMax: '',
    solar: '',
    hoa: '',
    taxRate: '',
    realtorName: '',
    realtorPhone: '',
    realtorEmail: '',
    rapport: '',
    urgency: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    severity: 'success',
    message: '',
  });

  const taxRateOverride = cfg.fieldLabelOverrides?.taxRate || '';
  const taxRateIsMoney = Boolean(taxRateOverride);

  const hidden = useMemo(
    () => new Set((cfg.hiddenFields || []).filter((f) => HIDEABLE_FIELDS.includes(f))),
    [cfg.hiddenFields]
  );
  const show = (name) => !hidden.has(name);

  // Per-division dynamic fields; ignore malformed rows so a bad config can
  // never blank the whole form.
  const customFields = useMemo(
    () => (Array.isArray(cfg.customFields) ? cfg.customFields.filter((f) => f && f.key && f.label) : []),
    [cfg.customFields]
  );
  const [customValues, setCustomValues] = useState({});
  const customRefs = useRef({});

  const onCustomChange = (key, value) =>
    setCustomValues((v) => ({ ...v, [key]: value }));

  const fieldRefs = {
     loanOfficer: useRef(null),
    agentName: useRef(null),
    agentPhone: useRef(null),
    agentEmail: useRef(null),
    buyerFirst: useRef(null),
    buyerLast: useRef(null),
    buyerEmail: useRef(null),
    buyerPhone: useRef(null),
    community: useRef(null),
    zip: useRef(null),
    fwdCommit: useRef(null),
    estClose: useRef(null),
    rapport: useRef(null),
    realtorPhone: useRef(null),
    realtorEmail: useRef(null),
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    let next = value;
    if (name === 'agentPhone' || name === 'buyerPhone' || name === 'realtorPhone') {
      next = maskPhone(value);
    } else if (name === 'zip') {
      next = maskZip(value);
    } else if (name === 'price' || name === 'hoa') {
      next = maskMoney(value);
    } else if (name === 'taxRate') {
      next = taxRateIsMoney ? maskMoney(value) : maskPercent(value);
    }
    setForm((f) => ({ ...f, [name]: next }));
  };

  const validate = () => {
    const e = {};

      if (Array.isArray(cfg.loanOfficers) && cfg.loanOfficers.length > 0) {
      if (!form.loanOfficer) e.loanOfficer = 'Required';
    }

    if (!form.agentName) e.agentName = 'Required';

    if (!form.agentPhone) e.agentPhone = 'Required';
    else if (!isValidPhone(form.agentPhone)) e.agentPhone = 'Must be 10 digits (XXX-XXX-XXXX)';

    if (!form.agentEmail) e.agentEmail = 'Required';
    else if (!isValidBuilderEmail(form.agentEmail)) {
      e.agentEmail = `Must be a ${builderEmailDomainsLabel()} address`;
    }

    if (!form.buyerFirst) e.buyerFirst = 'Required';
    if (!form.buyerLast) e.buyerLast = 'Required';

    if (!form.buyerEmail) e.buyerEmail = 'Required';
    else if (!isValidGenericEmail(form.buyerEmail)) {
      e.buyerEmail = 'Enter a valid email (e.g., name@example.com)';
    }

    if (!form.buyerPhone) e.buyerPhone = 'Required';
    else if (!isValidPhone(form.buyerPhone)) e.buyerPhone = 'Must be 10 digits (XXX-XXX-XXXX)';

    if (!form.community) e.community = 'Required';

    if (!form.zip) e.zip = 'Required';
    else if (isZipAllZeros(form.zip)) e.zip = 'Zip cannot be all zeros';
    else if (!isValidZip(form.zip)) e.zip = 'Must be 5 digits';

    if (form.fwdCommit === '') e.fwdCommit = 'Required';
    if (!form.estClose) e.estClose = 'Required';
    if (!form.rapport) e.rapport = 'Required';

    if (form.realtorPhone && !isValidPhone(form.realtorPhone)) {
      e.realtorPhone = 'Must be 10 digits (XXX-XXX-XXXX)';
    }
    if (form.realtorEmail && !isValidGenericEmail(form.realtorEmail)) {
      e.realtorEmail = 'Enter a valid email (e.g., name@example.com)';
    }

    customFields.forEach((f) => {
      if (!f.required) return;
      const val = customValues[f.key];
      const empty = f.type === 'checkbox' ? !val : !val || String(val).trim() === '';
      if (empty) e[`cf_${f.key}`] = 'Required';
    });

    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const v = validate();
    setErrors(v);

    if (Object.keys(v).length > 0) {
      const firstKey = Object.keys(v)[0];
      const refEl = firstKey.startsWith('cf_')
        ? customRefs.current[firstKey.slice(3)]
        : fieldRefs[firstKey]?.current;
      if (refEl) {
        refEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const input = refEl.querySelector('input, textarea, select');
        if (input) input.focus();
      }
      setSnack({
        open: true,
        severity: 'error',
        message: 'Please complete all required fields.',
      });
      return;
    }

    try {
      setLoading(true);

      const payload = {
        slug,
        leadSource: cfg.candidSlug || cfg.title,
        referralSource: cfg.candidSlug || cfg.title,
          // ✅ Lead model–correct fields
  firstName: form.buyerFirst,
  lastName: form.buyerLast,
  phoneNumber: form.buyerPhone,
  emailAddress: form.buyerEmail,

  // ✅ keep everything else
  loanOfficer: form.loanOfficer,
  agentName: form.agentName,
  agentPhone: form.agentPhone,
  agentEmail: form.agentEmail,
  community: form.community,
  zip: form.zip,
  lot: form.lot,
  price: stripMoney(form.price),
  fwdCommit: form.fwdCommit,
  estClose: form.estClose,
  incentiveMax: form.incentiveMax,
  solar: form.solar,
  hoa: stripMoney(form.hoa),
  taxRate: taxRateIsMoney ? stripMoney(form.taxRate) : form.taxRate,
  realtorName: form.realtorName,
  realtorPhone: form.realtorPhone,
  realtorEmail: form.realtorEmail,
  additionalDetails: form.rapport,
  urgency: form.urgency,
  state: cfg.state || '',
      };

      // Hidden built-ins are omitted from the payload entirely.
      HIDEABLE_FIELDS.forEach((name) => {
        if (!show(name)) delete payload[name];
      });

      if (customFields.length) {
        payload.customAnswers = customFields.map((f) => ({
          key: f.key,
          label: f.label,
          value: f.type === 'checkbox'
            ? (customValues[f.key] ? 'Yes' : 'No')
            : (customValues[f.key] ?? ''),
        }));
      }

      const resp = await apipostPublic(cfg.endpoint, payload);
      const success = !!resp?.data?.lead?._id;
      if (!success) throw new Error(resp?.data?.message || 'Request failed');

      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 5000);

      setSnack({
        open: true,
        severity: 'success',
        message: 'Submitted! A Jet HomeLoans teammate will reach out shortly.',
      });

      setForm({
        loanOfficer: '',
        agentName: '',
        agentPhone: '',
        agentEmail: '',
        buyerFirst: '',
        buyerLast: '',
        buyerEmail: '',
        buyerPhone: '',
        community: '',
        zip: '',
        lot: '',
        price: '',
        fwdCommit: '',
        estClose: '',
        incentiveMax: '',
        solar: '',
        hoa: '',
        taxRate: '',
        realtorName: '',
        realtorPhone: '',
        realtorEmail: '',
        rapport: '',
        urgency: '',
      });
      setCustomValues({});
    } catch (err) {
      setSnack({
        open: true,
        severity: 'error',
        message: err?.message || 'Something went wrong. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ConfettiModal open={showConfetti} onClose={() => setShowConfetti(false)} />
      <LandingPageNav />

      <Container maxWidth="lg" sx={{ pt: { xs: 10, md: 12 }, pb: { xs: 6, md: 10 } }}>
        <Card elevation={4} sx={{ borderRadius: 2 }}>
          {/* Brand bar */}
          <Box
            sx={{
              bgcolor: cfg.brandColor,
              py: 4,
              px: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
            }}
          >
            {cfg.logoSrc && (
              <Box
                component="img"
                src={cfg.logoSrc}
                alt={`${cfg.title} logo`}
                sx={{ height: 60, objectFit: 'contain' }}
              />
            )}
          </Box>

          <CardContent sx={{ p: { xs: 3, md: 5 } }}>
            <Stack spacing={1} alignItems="center" sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="h1" sx={{ color: '#2e3a49' }}>
                {cfg.title}
              </Typography>
              <Typography
                variant="h5"
                component="p"
                sx={{ letterSpacing: 4, color: 'text.secondary' }}
              >
                {cfg.subtitle}
              </Typography>
              <Typography
                variant="body1"
                sx={{ mt: 2, color: 'text.secondary' }}
              >
                {cfg.intro}
              </Typography>
            </Stack>

            {/* ===== FORM ===== */}
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Grid container spacing={2}>

                {/* Loan Officer */}
             {Array.isArray(cfg.loanOfficers) && cfg.loanOfficers.length > 0 && (
  <Grid item xs={12} ref={fieldRefs.loanOfficer}>
    <TextField
      required // ✅ add
      select
      fullWidth
      label="Loan Officer"
      name="loanOfficer"
      value={form.loanOfficer}
      onChange={onChange}
      error={!!errors.loanOfficer}
      helperText={errors.loanOfficer}
    >
      <MenuItem value="">
        <em>Select…</em>
      </MenuItem>

      {cfg.loanOfficers
        .filter((lo) => lo !== 'Select…')
        .map((lo) => (
          <MenuItem key={lo} value={lo}>
            {lo}
          </MenuItem>
        ))}
    </TextField>
  </Grid>
)}


                {/* Agent Name */}
                <Grid item xs={12} md={6} ref={fieldRefs.agentName}>
                  {Array.isArray(cfg.agentNames) && cfg.agentNames.length > 0 ? (
                    <TextField
                      required
                      select
                      fullWidth
                      label="Agent Name"
                      name="agentName"
                      value={form.agentName}
                      onChange={onChange}
                      error={!!errors.agentName}
                      helperText={errors.agentName}
                    >
                      <MenuItem value="">
                        <em>Select Agent…</em>
                      </MenuItem>
                      {cfg.agentNames.map((name) => (
                        <MenuItem key={name} value={name}>
                          {name}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : (
                    <TextField
                      required
                      fullWidth
                      label="Agent Name"
                      name="agentName"
                      value={form.agentName}
                      onChange={onChange}
                      error={!!errors.agentName}
                      helperText={errors.agentName}
                    />
                  )}
                </Grid>

                <Grid item xs={12} md={6} ref={fieldRefs.agentPhone}>
                  <TextField
                    required
                    fullWidth
                    label="Agent Phone"
                    name="agentPhone"
                    value={form.agentPhone}
                    onChange={onChange}
                    error={!!errors.agentPhone}
                    helperText={errors.agentPhone}
                  />
                </Grid>

                <Grid item xs={12} ref={fieldRefs.agentEmail}>
                  <TextField
                    required
                    fullWidth
                    label="Agent Email"
                    name="agentEmail"
                    type="email"
                    value={form.agentEmail}
                    onChange={onChange}
                    error={!!errors.agentEmail}
                    helperText={errors.agentEmail}
                  />
                </Grid>

                {/* Buyer */}
                <Grid item xs={12} md={6} ref={fieldRefs.buyerFirst}>
                  <TextField
                    required
                    fullWidth
                    label="Buyer First Name"
                    name="buyerFirst"
                    value={form.buyerFirst}
                    onChange={onChange}
                    error={!!errors.buyerFirst}
                    helperText={errors.buyerFirst}
                  />
                </Grid>
                <Grid item xs={12} md={6} ref={fieldRefs.buyerLast}>
                  <TextField
                    required
                    fullWidth
                    label="Buyer Last Name"
                    name="buyerLast"
                    value={form.buyerLast}
                    onChange={onChange}
                    error={!!errors.buyerLast}
                    helperText={errors.buyerLast}
                  />
                </Grid>
                <Grid item xs={12} md={6} ref={fieldRefs.buyerEmail}>
                  <TextField
                    required
                    fullWidth
                    label="Buyer Email"
                    name="buyerEmail"
                    type="email"
                    value={form.buyerEmail}
                    onChange={onChange}
                    error={!!errors.buyerEmail}
                    helperText={errors.buyerEmail}
                  />
                </Grid>
                <Grid item xs={12} md={6} ref={fieldRefs.buyerPhone}>
                  <TextField
                    required
                    fullWidth
                    label="Buyer Phone"
                    name="buyerPhone"
                    value={form.buyerPhone}
                    onChange={onChange}
                    error={!!errors.buyerPhone}
                    helperText={errors.buyerPhone}
                  />
                </Grid>

                {/* Community & Property */}
                <Grid item xs={12} md={6} ref={fieldRefs.community}>
                  <TextField
                    select
                    required
                    fullWidth
                    label="Select Community"
                    name="community"
                    value={form.community}
                    onChange={onChange}
                    error={!!errors.community}
                    helperText={errors.community}
                  >
                    {cfg.communities.map((c) => (
                      <MenuItem key={c} value={c === 'Select Community…' ? '' : c}>
                        {c}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6} ref={fieldRefs.zip}>
                  <TextField
                    required
                    fullWidth
                    label="Zip Code "
                    name="zip"
                    value={form.zip}
                    onChange={onChange}
                    error={!!errors.zip}
                    helperText={errors.zip}
                  />
                </Grid>

                {show('lot') && (
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Lot Number" name="lot" value={form.lot} onChange={onChange} />
                </Grid>
                )}
                {show('price') && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Sales Price ($)"
                    name="price"
                    value={form.price}
                    onChange={onChange}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  />
                </Grid>
                )}

                <Grid item xs={12} md={6} ref={fieldRefs.fwdCommit}>
                  <TextField
                  required
                    select
                    fullWidth
                    label="Forward Commitment"
                    name="fwdCommit"
                    value={form.fwdCommit}
                    onChange={onChange}
                    error={!!errors.fwdCommit}
                    helperText={errors.fwdCommit}
                  >
                    {cfg.forwardCommitments.map((opt) => (
                      <MenuItem key={opt} value={opt === 'None' ? 'None' : opt}>{opt}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6} ref={fieldRefs.estClose}>
                  <TextField
                    required
                    fullWidth
                    type="date"
                    label="Est Closing/Completion Date"
                    name="estClose"
                    value={form.estClose}
                    onChange={onChange}
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.estClose}
                    helperText={errors.estClose}
                  />
                </Grid>

                {show('incentiveMax') && (
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Incentive Available Max" name="incentiveMax" value={form.incentiveMax} onChange={onChange} />
                </Grid>
                )}
                {show('solar') && (
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Solar"
                    name="solar"
                    value={form.solar}
                    onChange={onChange}
                    helperText="Select whether this home includes a solar energy system."
                  >
                    <MenuItem value=""><em>Select…</em></MenuItem>
                    <MenuItem value="YES">YES</MenuItem>
                    <MenuItem value="NO">NO</MenuItem>
                  </TextField>
                </Grid>
                )}

                {show('hoa') && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="HOA ($)"
                    name="hoa"
                    value={form.hoa}
                    onChange={onChange}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  />
                </Grid>
                )}
                {show('taxRate') && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={taxRateOverride || 'Tax Rate (%)'}
                    name="taxRate"
                    value={form.taxRate}
                    onChange={onChange}
                    InputProps={
                      taxRateIsMoney
                        ? { startAdornment: <InputAdornment position="start">$</InputAdornment> }
                        : { endAdornment: <InputAdornment position="end">%</InputAdornment> }
                    }
                  />
                </Grid>
                )}

                {/* Realtor */}
                {show('realtorName') && (
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Realtor Name" name="realtorName" value={form.realtorName} onChange={onChange} />
                </Grid>
                )}
                {show('realtorPhone') && (
                <Grid item xs={12} md={6} ref={fieldRefs.realtorPhone}>
                  <TextField
                    fullWidth
                    label="Realtor Phone"
                    name="realtorPhone"
                    value={form.realtorPhone}
                    onChange={onChange}
                    error={!!errors.realtorPhone}
                    helperText={errors.realtorPhone}
                  />
                </Grid>
                )}
                {show('realtorEmail') && (
                <Grid item xs={12} ref={fieldRefs.realtorEmail}>
                  <TextField
                    fullWidth
                    label="Realtor Email"
                    name="realtorEmail"
                    type="email"
                    value={form.realtorEmail}
                    onChange={onChange}
                    error={!!errors.realtorEmail}
                    helperText={errors.realtorEmail}
                  />
                </Grid>
                )}

                {/* Rapport */}
                <Grid item xs={12} ref={fieldRefs.rapport}>
                  <TextField
                    required
                    fullWidth
                    multiline
                    minRows={5}
                    label="Anything you can tell us to build rapport? "
                    name="rapport"
                    value={form.rapport}
                    onChange={onChange}
                    error={!!errors.rapport}
                    helperText={errors.rapport}
                  />
                </Grid>

                {/* Urgency */}
                {show('urgency') && (
                <Grid item xs={12} ref={fieldRefs.urgency}>
                  <TextField
                    select
                    fullWidth
                    label="Urgency "
                    name="urgency"
                    value={form.urgency}
                    onChange={onChange}
                    error={!!errors.urgency}
                    helperText={errors.urgency}
                  >
                    {cfg.urgency.map((u) => (
                      <MenuItem key={u} value={u}>{u}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                )}

                {/* Per-division dynamic fields (BuilderIntake.customFields) */}
                {customFields.map((f) => (
                  <Grid
                    item
                    xs={12}
                    md={6}
                    key={f.key}
                    ref={(el) => { customRefs.current[f.key] = el; }}
                  >
                    {f.type === 'select' ? (
                      <TextField
                        select
                        required={!!f.required}
                        fullWidth
                        label={f.label}
                        value={customValues[f.key] ?? ''}
                        onChange={(e) => onCustomChange(f.key, e.target.value)}
                        error={!!errors[`cf_${f.key}`]}
                        helperText={errors[`cf_${f.key}`]}
                      >
                        <MenuItem value=""><em>Select…</em></MenuItem>
                        {(f.options || []).map((opt) => (
                          <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                        ))}
                      </TextField>
                    ) : f.type === 'checkbox' ? (
                      <FormControl required={!!f.required} error={!!errors[`cf_${f.key}`]}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!customValues[f.key]}
                              onChange={(e) => onCustomChange(f.key, e.target.checked)}
                            />
                          }
                          label={f.required ? `${f.label} *` : f.label}
                        />
                        {errors[`cf_${f.key}`] && (
                          <FormHelperText>{errors[`cf_${f.key}`]}</FormHelperText>
                        )}
                      </FormControl>
                    ) : (
                      <TextField
                        required={!!f.required}
                        fullWidth
                        label={f.label}
                        type={f.type === 'date' ? 'date' : 'text'}
                        InputLabelProps={f.type === 'date' ? { shrink: true } : undefined}
                        value={customValues[f.key] ?? ''}
                        onChange={(e) => onCustomChange(f.key, e.target.value)}
                        error={!!errors[`cf_${f.key}`]}
                        helperText={errors[`cf_${f.key}`]}
                      />
                    )}
                  </Grid>
                ))}

                <Grid item xs={12}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading}
                    sx={{
                      bgcolor: '#3e4b59',
                      '&:hover': { bgcolor: '#2e3946' },
                      py: 1.4,
                      letterSpacing: 1,
                    }}
                  >
                    {loading ? 'SUBMITTING…' : 'SUBMIT'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      </Container>

      <Footer />

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
      <ConfettiModal open={showConfetti} onClose={() => setShowConfetti(false)} />
    </>
  );
}
